// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Journal entries table component for accounting close workflow.
 *
 * Displays a table of journal entries with expandable line items, export and review actions.
 * Each row can be expanded to show the journal entry's line items with account, description, debit, and credit columns.
 * A row can be flashed (scrolled to + briefly highlighted) via `flashEntryId` to point the user at
 * the next entry that still needs review.
 */
import { JournalEntryWithReviewState } from '@/accounting/api/types';
import { ReviewStatusChip } from '@/accounting/components/review-status-chip';
import {
  CheckRounded,
  DownloadRounded,
  ExpandMoreRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { useDownloadFileByKey } from '@/hooks/use-download-file-by-key';
import { useFocusHighlight } from '@/hooks/use-focus-highlight';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import { Fragment, useState } from 'react';

interface Props {
  entries: JournalEntryWithReviewState[];
  onMarkReviewed: (entryId: string) => void;
  currencyCode: IsoCurrencyCode;
  // Entry to scroll to and flash (the next one needing review); null flashes nothing.
  flashEntryId: string | null;
  // Called once a flash finishes so the parent can clear flashEntryId and allow re-triggering.
  onFlashComplete: (entryId: string) => void;
}

interface JournalEntryRowProps {
  entry: JournalEntryWithReviewState;
  onMarkReviewed: (entryId: string) => void;
  currencyCode: IsoCurrencyCode;
  isFlashing: boolean;
  onFlashComplete: (entryId: string) => void;
}

function JournalEntryRow(props: JournalEntryRowProps) {
  const downloadCsv = useDownloadFileByKey();
  const [isExpanded, setIsExpanded] = useState(false);
  const entry = props.entry;
  const { ref, isHighlighted } = useFocusHighlight<HTMLTableRowElement>({
    isFocused: props.isFlashing,
    onComplete: () => props.onFlashComplete(entry.id),
  });

  const isEmpty = entry.lineItems.length === 0;
  const isReviewedOrEmpty = entry.reviewed || isEmpty;

  return (
    <Fragment>
      <TableRow
        ref={ref}
        sx={{
          transition: 'background-color 0.4s ease',
          backgroundColor: isHighlighted
            ? (theme) => alpha(theme.palette.primary.main, 0.12)
            : undefined,
        }}
      >
        <TableCell sx={{ width: 48, paddingY: 0 }}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={
              isExpanded ? 'Collapse line items' : 'Expand line items'
            }
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreRounded fontSize="small" />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}>
          {entry.externalId}
        </TableCell>
        <TableCell>{entry.memo}</TableCell>
        <TableCell align="right">
          {isEmpty ? '—' : entry.lineItems.length}
        </TableCell>
        <TableCell align="right">
          {formatAmountWithCurrency(entry.totalDebits, props.currencyCode, {
            sparse: true,
          })}
        </TableCell>
        <TableCell align="right">
          {formatAmountWithCurrency(entry.totalCredits, props.currencyCode, {
            sparse: true,
          })}
        </TableCell>
        <TableCell align="center">
          <ReviewStatusChip isReviewed={isReviewedOrEmpty} />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadRounded fontSize="small" />}
              disabled={!entry.csvFileKey || downloadCsv.isPending}
              onClick={() => downloadCsv.mutate(entry.csvFileKey)}
            >
              Export
            </Button>
            <Button
              size="small"
              variant={isReviewedOrEmpty ? 'outlined' : 'contained'}
              color="secondary"
              disabled={isReviewedOrEmpty}
              onClick={() => props.onMarkReviewed(entry.id)}
              startIcon={
                isReviewedOrEmpty ? (
                  <CheckRounded fontSize="small" />
                ) : undefined
              }
            >
              {isEmpty
                ? 'No review needed'
                : entry.reviewed
                  ? 'Reviewed'
                  : 'Mark reviewed'}
            </Button>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          sx={{ paddingY: 0, paddingBottom: isExpanded ? 2 : 0 }}
          colSpan={8}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {isEmpty ? (
                <Box
                  sx={{
                    padding: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  No line items for this period
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entry.lineItems.map((lineItem) => (
                      <TableRow key={lineItem.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {lineItem.ledgerAccountName}
                        </TableCell>
                        <TableCell>{lineItem.description}</TableCell>
                        <TableCell align="right">
                          {parseFloat(lineItem.debit) > 0
                            ? formatAmountWithCurrency(
                                lineItem.debit,
                                props.currencyCode,
                                { sparse: true }
                              )
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(lineItem.credit) > 0
                            ? formatAmountWithCurrency(
                                lineItem.credit,
                                props.currencyCode,
                                { sparse: true }
                              )
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatAmountWithCurrency(
                          entry.totalDebits,
                          props.currencyCode,
                          { sparse: true }
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatAmountWithCurrency(
                          entry.totalCredits,
                          props.currencyCode,
                          { sparse: true }
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

export function JournalEntriesTable(props: Props) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }} />
            <TableCell>Journal ID</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Lines</TableCell>
            <TableCell align="right">Total Debits</TableCell>
            <TableCell align="right">Total Credits</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.entries.map((entry) => (
            <JournalEntryRow
              key={entry.id}
              entry={entry}
              onMarkReviewed={props.onMarkReviewed}
              currencyCode={props.currencyCode}
              isFlashing={props.flashEntryId === entry.id}
              onFlashComplete={props.onFlashComplete}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
